import {
    Box,
    Button,
    ClickAwayListener,
    Drawer,
    Grow,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuList,
    Paper,
    Popper,
    Stack, Typography,
} from "@mui/material";
import { getPages } from "../../app/Pages";
import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import logo from "../../images/new_logo.png";
import { AccountCircleOutlined, LoginOutlined, LogoutOutlined } from '@mui/icons-material';
import { useDispatch } from "react-redux";
import { apiSlice } from "../../api/apiSlice";
import { colours } from "../../StyleConstants";
import DatabaseSelector from "../DatabaseSelector";
import SidebarItem from "./sidebar/SidebarItem";
import DropdownSidebarItem from "./sidebar/DropdownSidebarItem";
import keycloak from "../../Keycloak";
import { useGlobusAuth } from "@globus/react-auth-context";
import { useUserInfoQuery } from "../../api/userApiSlice";
import { deleteCookie } from '../../utils';
import useLassoSafeController from "../../features/lasso-safe/hooks/useLassoSafeController";
import LassoSafeModal from "../../features/lasso-safe/LassoSafeModal";

export const SIDEBAR_FULL_WIDTH = 200;
export const SIDEBAR_SMALL_WIDTH = 50;

interface Props {
    open: boolean;
}

const Sidebar = (props: Props) => {
    const { open } = props;

    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const lassoSafeController = useLassoSafeController();
    const [selected, setSelected] = useState<string>("");
    const { data: userData, isFetching: userLoading } = useUserInfoQuery({});
    const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
    const anchorRef = useRef<HTMLButtonElement>(null);
    const { authorization, isAuthenticated } = useGlobusAuth();
    const isGuestUser = userData?.id === -1

    useEffect(() => {
        const pathname = location.pathname.replace(/\/\s*$/, "");
        const paths = pathname.split("/");
        setSelected(paths.length === 1 ? "" : paths[1]);
    }, [location]);

    if (userLoading) {
        return <div>loading...</div>;
    }

    const userRoles = userData?.roles || [];
    const onlyDuc = userRoles.includes("ADMIN") ? false : window?.env?.onlyDuc;
    const handleClose = (event: Event | React.SyntheticEvent) => {
        if (
            anchorRef.current &&
            anchorRef.current.contains(event.target as HTMLElement)
        ) {
            return;
        }

        setIsProfileMenuOpen(false);
    };

    function handleListKeyDown(event: React.KeyboardEvent) {
        if (event.key === "Tab") {
            event.preventDefault();
            setIsProfileMenuOpen(false);
        } else if (event.key === "Escape") {
            setIsProfileMenuOpen(false);
        }
    }

    const handleToggle = () => {
        setIsProfileMenuOpen((prevOpen) => !prevOpen);
    };


    const getMenuItems = (pages: any[]) => {
        let menuItems: any[] = [];
        const userRoles = userData?.roles || [];
        pages.forEach((page: any) => {
            if (page.requiredRoles && !page.requiredRoles.some((requiredRole: string) => userRoles.includes(requiredRole))) {
                return;
            }
            if (page?.disabled) {
                return
            }

            if (page.sidebar) {
                menuItems.push({
                    type: page.type,
                    path: page.path ?? "",
                    isDropdown: page.sidebar.isDropdown ?? false,
                    dropdownMenu: page.sidebar.isDropdown ? page.sidebar.dropdownMenu : [],
                    label: page.sidebar.label,
                    icon: page.sidebar.icon,
                    hoverTooltip: page.sidebar.hoverTooltip,
                });
            }
            if (page.children) {
                menuItems = menuItems.concat(getMenuItems(page.children));
            }
        });

        return menuItems;
    };

    const login = () => {
        keycloak.login();
    }

    const logout = () => {
        setIsProfileMenuOpen(false);
        dispatch(apiSlice.util.resetApiState());
        if (isAuthenticated) {
            authorization?.reset();
        }
        deleteCookie("JWT_TOKEN");
        keycloak.logout();
    };

    return (
        <Box
            sx={{
                width: (open ? SIDEBAR_FULL_WIDTH : SIDEBAR_SMALL_WIDTH) + 16,
                height: "100%",
                backgroundColor: "primary.dark",
                flexShrink: 0,
            }}
        >
            <Drawer
                open={true}
                variant={"persistent"}
                PaperProps={{
                    sx: {
                        width:
                            (open ? SIDEBAR_FULL_WIDTH : SIDEBAR_SMALL_WIDTH) +
                            16, // Adding for padding (left&right)
                        backgroundColor: "primary.dark",
                        color: "white",
                        borderRight: 0,
                    },
                }}
            >
                <Box
                    sx={{ height: 1, display: "flex", flexDirection: "column", alignItems: "center" }}
                >
                    <img
                        id="sidebar-logo"
                        src={logo}
                        alt="Lasso Informatics logo"
                        style={{ width: 144, height: 100, aspectRatio: 1, marginBlockStart: "1rem" }}
                        tabIndex={0}
                    />
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            flexGrow: 1,
                            overflowX: "auto",
                            marginTop: "3rem",
                            gap: 3
                        }}
                    >
                        <DatabaseSelector />
                        <List
                            disablePadding
                            sx={{
                                paddingLeft: 2,
                                paddingTop: 3,
                                gap: "0.5rem",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            {getMenuItems(getPages(onlyDuc)).map((item) => {
                                // Hack for LC-2598 to rename sidebar label if you're not logged in
                                const label =  typeof item.label === 'function'
                                    ? item.label(userData)
                                    : item.label;
                                if(item.type === "link"){
                                    return  (
                                        <SidebarItem
                                            key={item.label}
                                            id={`sidebar-nav-link-${item.label}`}
                                            selected={item.path === selected}
                                            label={label}
                                            icon={item.icon}
                                            onClick={lassoSafeController.openLassoSafe}
                                            hoverTooltip={item.hoverTooltip}
                                        />
                                    )
                                }
                                return (item.isDropdown
                                    ? <DropdownSidebarItem
                                        key={item.label}
                                        id={`sidebar-nav-link-${item.label}`}
                                        selected={item.path === selected}
                                        icon={item.icon}
                                        label={label}
                                        menu={item.dropdownMenu}
                                    />
                                    : <SidebarItem
                                        key={item.label}
                                        id={`sidebar-nav-link-${item.label}`}
                                        selected={item.path === selected}
                                        label={label}
                                        icon={item.icon}
                                        onClick={() => { navigate(item.path) }}
                                        hoverTooltip={item.hoverTooltip}
                                    />
                                )
                            }

                            )}
                        </List>
                    </Box>
                    {
                        isGuestUser ?
                            <Button
                                variant={"primary"}
                                onClick={login}
                                id="sidebar-login-button"
                                startIcon={<LoginOutlined />}
                                sx={{
                                    marginY: 2,
                                    width: "calc(100% - 16px)", // This button is not respecting margin X values, so this is a workaround.
                                }}
                            >
                              Login
                            </Button>
                            :
                            <>                   <Button
                                ref={anchorRef}
                                id={"sidebar-user-account-button"}
                                aria-label="account of current user"
                                aria-controls="primary-search-account-menu"
                                aria-haspopup="true"
                                color="inherit"
                                sx={{
                                    alignSelf: "end",
                                    borderTop: "solid 0.25rem #EF9752",
                                    width: "110%",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    maxHeight: "3.75rem",
                                    gap: "1rem",
                                    color: "white",
                                    fontSize: "1rem",
                                    fontWeight: 400,
                                    textAlign: "center",
                                    lineHeight: "24px",
                                    borderRadius: 0
                                }}
                                onClick={handleToggle}
                                startIcon={ <AccountCircleOutlined
                                    sx={{ width: "2rem", aspectRatio: 1 }}
                                />}
                            >
                                {userData?.fullName}
                            </Button>
                            <Popper
                                open={isProfileMenuOpen}
                                anchorEl={anchorRef.current}
                                placement="right-end"
                                transition
                                sx={{ backgroundColor: colours.midnightBlue.blue5 }}
                            >
                                {({ TransitionProps }) => (
                                    <Grow {...TransitionProps}>
                                        <Paper sx={{ marginLeft: "0.25rem" }}>
                                            <ClickAwayListener
                                                onClickAway={handleClose}
                                            >
                                                <MenuList
                                                    autoFocusItem={isProfileMenuOpen}
                                                    id="composition-menu"
                                                    aria-labelledby="composition-button"
                                                    onKeyDown={handleListKeyDown}
                                                    sx={{ color: colours.midnightBlue.blue160 }}
                                                >
                                                    <Stack paddingY={2} paddingX={3}>
                                                        <Typography><span style={{ fontWeight: 600 }}>User:</span> <span id="sidebar-current-user-name">{userData?.username || "N/A"}</span></Typography>
                                                        <Typography><span style={{ fontWeight: 600 }}>Email:</span> <span id="sidebar-current-user-email">{userData?.email || "N/A"}</span></Typography>
                                                    </Stack>
                                                    <hr style={{
                                                        backgroundColor: "rgba(0, 0, 0, 0.12)",
                                                        border: "0 none",
                                                        color: "rgba(0, 0, 0, 0.12)",
                                                        height: "2px",
                                                    }}/>
                                                    <ListItemButton onClick={logout} disableRipple id="sidebar-logout-button">
                                                        <ListItemIcon>
                                                            <LogoutOutlined />
                                                        </ListItemIcon>
                                                        <ListItemText>
                                          Logout
                                                        </ListItemText>
                                                    </ListItemButton>
                                                </MenuList>
                                            </ClickAwayListener>
                                        </Paper>
                                    </Grow>
                                )}
                            </Popper>
                            </>
                    }

                </Box>
            </Drawer>

            <LassoSafeModal
                isOpen={lassoSafeController.isModalOpen}
                onClose={lassoSafeController.closeModal}
                state={lassoSafeController.lassoSafeState}
            />
        </Box>
    );
};

export default Sidebar;
